
 // pages/master/tabs/OldSlrmBillsTab.js

import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import {
  fetchSlrmBillsByHouseThunk,
  deleteSlrmBillThunk,
  clearSlrmBillsError,
} from "../../../../store/slices/slrmBillsSlice";

import "../../../../css/master-common.css";

// ============================================================
// DATE FORMATTERS
// ============================================================

const formatIndianDate = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "-";

  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;

  const mysqlDateTime = s.match(
    /^(\d{4})-(\d{2})-(\d{2}) \d{2}:\d{2}:\d{2}$/
  );

  if (mysqlDateTime) {
    return `${mysqlDateTime[3]}-${mysqlDateTime[2]}-${mysqlDateTime[1]}`;
  }

  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  })
    .format(date)
    .replace(/\//g, "-");
};

const formatIndianDateTime = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "-";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return formatIndianDate(s);
  }

  const mysqlDateTime = s.match(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/
  );

  if (mysqlDateTime) {
    const [, year, month, day, hours, minutes] = mysqlDateTime;

    let hour = Number(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;

    return `${day}-${month}-${year} ${String(hour).padStart(2, "0")}:${minutes} ${ampm}`;
  }

  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return "-";

  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  }).formatToParts(date);

  const get = (type) =>
    parts.find((part) => part.type === type)?.value || "";

  return `${get("day")}-${get("month")}-${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
};

const excelNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : value;
};

const excelDate = (value, withTime = false) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const formatted = withTime
    ? formatIndianDateTime(value)
    : formatIndianDate(value);

  return formatted === "-" ? "" : formatted;
};

const formatAmount = (value) =>
  Number(value || 0).toFixed(2);

// ============================================================
// DELETE CONFIRMATION
// ============================================================

const confirmToast = (message, { onYes, onNo } = {}) => {
  return toast(
    ({ closeToast }) => (
      <div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          {message}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              closeToast?.();
              onYes?.();
            }}
          >
            Yes, Delete
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              closeToast?.();
              onNo?.();
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    ),
    {
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
    }
  );
};

// ============================================================
// SLRM BILLS TAB
// ============================================================

export default function OldSlrmBillsTab({
  home_id,
  activeTab,
  onRefreshProfile,
}) {
  const dispatch = useDispatch();

  const { list = [], loading, error, deletingId } = useSelector(
    (s) => s.slrmBills
  );

  const [q, setQ] = useState("");

  const isActive = activeTab === "slrm_old_bills";

  // Load SLRM bills when tab opens
  useEffect(() => {
    if (!isActive || !home_id) return;

    dispatch(fetchSlrmBillsByHouseThunk(home_id));
  }, [isActive, home_id, dispatch]);

  // API error handling
  useEffect(() => {
    if (!error) return;

    toast.error(String(error));
    dispatch(clearSlrmBillsError());
  }, [error, dispatch]);

  // ============================================================
  // SEARCH
  // ============================================================

  const filtered = useMemo(() => {
    const query = String(q || "").toLowerCase().trim();

    if (!query) return list || [];

    return (list || []).filter((x) => {
      const hay = [
        x.sb_id,
        x.qr_code,
        x.family_head,
        x.door_no,
        x.ward_name,
        x.created_by_name,
        x.created_by,
        x.s_bill_date,
        x.created_on,
        x.slrm_last_bill_date,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");

      return hay.includes(query);
    });
  }, [list, q]);

  // ============================================================
  // EXPORT EXCEL
  // ============================================================

  const exportExcel = () => {
    if (!filtered.length) {
      toast.info("No SLRM bills to export");
      return;
    }

    try {
      const data = filtered.map((x, index) => ({
        "Sl No": index + 1,
        "Bill No": x.sb_id ?? "",
        "House No": x.qr_code || x.door_no || "",
        "Name": x.family_head || "",
        "Ward Name": x.ward_name || "",

        "Bill Date": excelDate(x.s_bill_date),
        "Bill Generated Date": excelDate(x.created_on, true),
        "Last SLRM Bill Date": excelDate(x.slrm_last_bill_date),

        "Total Days": excelNumber(x.s_total_days),
        "Per Day": excelNumber(x.per_day_charge),
        "SLRM Reading Fee": excelNumber(x.slrm_reading_fee),
        "Bill Amount": excelNumber(x.bill_amount),
        "Old Due": excelNumber(x.old_due),
        "Total Bill": excelNumber(x.total_bill),

        "Created By": x.created_by_name || x.created_by || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);

      worksheet["!cols"] = Object.keys(data[0]).map((header) => {
        const maxLength = data.reduce(
          (max, row) =>
            Math.max(max, String(row[header] ?? "").length),
          header.length
        );

        return {
          wch: Math.min(Math.max(maxLength + 3, 14), 38),
        };
      });

      const lastCell = XLSX.utils.encode_cell({
        r: data.length,
        c: Object.keys(data[0]).length - 1,
      });

      worksheet["!autofilter"] = {
        ref: `A1:${lastCell}`,
      };

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "SLRM Bills"
      );

      XLSX.writeFile(
        workbook,
        `SLRM_Bills_House_${home_id}.xlsx`
      );

      toast.success("SLRM bills Excel exported successfully");
    } catch (err) {
      console.error("SLRM Excel Export Error:", err);
      toast.error("Failed to export SLRM bills");
    }
  };

  // ============================================================
  // REFRESH
  // ============================================================

  const onRefresh = () => {
    if (!home_id) return;

    dispatch(fetchSlrmBillsByHouseThunk(home_id));
    toast.info("Refreshing...");
  };

  // ============================================================
  // DELETE
  // ============================================================

  const doDelete = async (sb_id) => {
    const r = await dispatch(deleteSlrmBillThunk({ sb_id }));

    if (deleteSlrmBillThunk.fulfilled.match(r)) {
      toast.success(`SLRM Bill #${sb_id} deleted`);

      dispatch(fetchSlrmBillsByHouseThunk(home_id));

      onRefreshProfile?.();
    } else {
      const msg =
        r?.payload?.message ||
        r?.payload ||
        r?.error?.message ||
        "Failed to delete SLRM bill";

      toast.error(String(msg));
    }
  };

  const onDelete = (row) => {
    if (!row?.sb_id) return;

    confirmToast(`Delete SLRM Bill #${row.sb_id}?`, {
      onYes: () => doDelete(row.sb_id),
    });
  };

  if (!isActive) return null;

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="hm-form-page old-slrm-bills-tab">
      <Card className="hm-form-card">
        <Card.Header className="hm-form-header">
          <div className="hm-header-row">
            <div className="hm-header-left">
              <h5 className="mb-1 hm-form-title">
                All SLRM Bills
              </h5>

              <div className="hm-form-subtitle">
                Search & manage all SLRM bills (delete if needed).
              </div>
            </div>

            <div className="hm-header-right d-flex gap-2 align-items-center">
              <Button
                size="sm"
                variant="outline-success"
                className="master-btn"
                onClick={exportExcel}
                disabled={loading || !filtered.length}
              >
                Export Excel
              </Button>

              <Button
                size="sm"
                variant="outline-secondary"
                className="master-btn"
                onClick={onRefresh}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>

              <div className="hm-search-box">
                <Form.Control
                  className="hm-control hm-search-input-right"
                  size="sm"
                  placeholder="Search bill no / name / ward / qr..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="hm-form-body">
          {loading ? (
            <div className="hm-form-loading">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">
                Loading SLRM bills...
              </div>
            </div>
          ) : (
            <div className="master-table-card">
              <div className="master-table-wrap">
                <Table
                  bordered
                  hover
                  size="sm"
                  className="mb-0 master-eq-table master-sticky master-row-hover old-slrm-bills-table"
                >
                  <thead className="table-light">
                    <tr>
                      <th>Action</th>
                      <th>Bill No</th>
                      <th>House No</th>
                      <th>Name</th>
                      <th>Ward Name</th>
                      <th>Bill Date</th>
                      <th>Bill Generated Date</th>
                      <th>Last SLRM Bill Date</th>
                      <th>Total Days</th>
                      <th>Per Day</th>
                      <th>SLRM Reading Fee</th>
                      <th>Bill Amount</th>
                      <th>Old Due</th>
                      <th>Total Bill</th>
                      <th>Created By</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td
                          colSpan={15}
                          className="text-center text-muted py-4"
                        >
                          No SLRM bills found
                        </td>
                      </tr>
                    ) : (
                      filtered.map((x) => (
                        <tr key={x.sb_id}>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="master-btn"
                              onClick={() => onDelete(x)}
                              disabled={
                                String(deletingId) === String(x.sb_id)
                              }
                            >
                              {String(deletingId) === String(x.sb_id)
                                ? "Deleting..."
                                : "Delete"}
                            </Button>
                          </td>

                          <td>{x.sb_id}</td>
                          <td>{x.qr_code || "-"}</td>
                          <td>{x.family_head || "-"}</td>
                          <td>{x.ward_name || "-"}</td>

                          <td>{formatIndianDate(x.s_bill_date)}</td>

                          <td>
                            {formatIndianDateTime(x.created_on)}
                          </td>

                          <td>
                            {formatIndianDate(x.slrm_last_bill_date)}
                          </td>

                          <td>{x.s_total_days ?? "-"}</td>

                          <td>{formatAmount(x.per_day_charge)}</td>

                          <td>{formatAmount(x.slrm_reading_fee)}</td>

                          <td>{formatAmount(x.bill_amount)}</td>

                          <td>{formatAmount(x.old_due)}</td>

                          <td>{formatAmount(x.total_bill)}</td>

                          <td>
                            {x.created_by_name || x.created_by || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
